import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth, createSlug, useUser } from "../Context/UserContext";
import API_URL from "../config";
import { Bell } from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { cn } from "../lib/utils";

const NotificationsButton = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [fetchTriggered, setFetchTriggered] = useState(false);
  const { fetchUserInfo } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotifications = async () => {
      const currentUserId = localStorage.getItem("current_user_id");

      if (!currentUserId) {
        return;
      }

      try {
        const response = await fetchWithAuth(`${API_URL}/notify/notifications`);

        if (response.ok) {
          const data = await response.json();
          setNotifications(data);
          setUnreadCount(data.filter((notif) => !notif.leida).length);

          const requiresFetch = data.some(
            (notif) =>
              notif.tipo === "promocion" ||
              notif.tipo === "aceptacion" ||
              notif.tipo === "eliminacion"
          );

          if (requiresFetch && !fetchTriggered) {
            setFetchTriggered(true);
            await fetchUserInfo();
          }
        }
      } catch (error) {
        console.error("❌ Error al obtener notificaciones:", error);
      }
    };

    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/notify/notifications/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        console.error(
          `❌ Error en la solicitud PATCH (${response.status}):`,
          await response.text()
        );
        return;
      }

      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id_notification === id ? { ...notif, leida: true } : notif
        )
      );

      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.error("❌ Error al marcar notificación como leída:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    handleMarkAsRead(notification.id_notification);

    const targetPath =
      notification.tipo === "respuesta" || notification.tipo === "pregunta"
        ? `/ver-licitacion/${notification.id_tender}/${createSlug(
            notification.titulo_tender
          )}`
        : notification.tipo === "postulacion" ||
          notification.tipo === "eliminacion-postulacion" ||
          notification.tipo === "retiro-ganador"
        ? `/ver-postulaciones/${notification.id_tender}`
        : notification.tipo === "asociacion"
        ? `/company-profile/${notification.id_company}/admin/usuarios`
        : notification.tipo === "review"
        ? `/company-profile/${notification.id_company}/nombre-de-empresa`
        : notification.tipo === "promocion" ||
          notification.tipo === "aceptacion"
        ? `/profile/${notification.id_user}/nombre-de-usuario`
        : notification.tipo === "ganador"
        ? `/admin-contrato/${notification.id_application}`
        : notification.tipo === "eliminacion-ganador"
        ? `/detalle-postulacion/${notification.id_application}`
        : null;

    if (targetPath) {
      if (window.location.pathname === targetPath) {
        window.location.reload();
      } else {
        navigate(targetPath);
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative rounded-md p-2 hover:bg-muted" aria-label="Notificaciones">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 h-5 min-w-5 justify-center rounded-full px-1 text-[10px]"
            >
              {unreadCount}
            </Badge>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <DropdownMenuItem
              key={notification.id_notification}
              className={cn(
                "flex flex-col items-start gap-1 whitespace-normal",
                !notification.leida && "bg-primary/5"
              )}
              onClick={() => handleNotificationClick(notification)}
            >
              <p className="text-sm">{notification.mensaje}</p>
              <small className="text-xs text-muted-foreground">
                {new Date(notification.created_at).toLocaleString()}
              </small>
            </DropdownMenuItem>
          ))
        ) : (
          <p className="px-2 py-4 text-center text-sm text-muted-foreground">
            No tienes notificaciones
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsButton;
